import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList, FaEye } from "react-icons/fa";
import moment from "moment";
import styles from "./List.module.css";
import AxiosInstance, { TokenService } from "../../services/Api";
import { CustomButton } from "../../components";
import CustomMRTTable from "../../components/CustomMRTTable/CustomMRTTable";

const LeaveList = () => {
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const parent_id = clientData?.master_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { translations } = useSelector((state) => state.language);

  // Fetch leave list
  useEffect(() => {
    if (parent_id) {
      fetchLeaveList();
    }
  }, [parent_id]);

  const fetchLeaveList = async () => {
    setLoading(true);
    try {
      const payload = {
        parent_id: parseInt(parent_id),
      };

      const response = await AxiosInstance.post(
        `/attendance/listLeave`,
        payload,
      );

      if (response?.data?.status) {
        const leaveData = response.data.data || [];
        const transformedData = leaveData.map((item, index) => ({
          id: item.id,
          sno: index + 1,
          student_id: item.student_id,
          student_name: item.student_name || "",
          from_date: item.from_date
            ? moment(item.from_date).format("DD/MM/YYYY")
            : "",
          to_date: item.to_date
            ? moment(item.to_date).format("DD/MM/YYYY")
            : "",
          no_days: item.no_days || 0,
          reason: item.reason,
          messages: item.messages || "",
          status: item.status,
          rattch_status: item.rattch_status || 0,
          isFlag: item.isFlag || 0,
        }));
        setData(transformedData);
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching leave list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Get status label and color
  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return {
          label: translations.labels?.accepted || "ACCEPTED",
          className: styles.statusAccepted,
        };
      case 2:
        return {
          label: translations.labels?.rejected || "REJECTED",
          className: styles.statusRejected,
        };
      case 4:
        return {
          label: translations.labels?.withdrawn || "WITHDRAWN",
          className: styles.statusWithdrawn,
        };
      default:
        return {
          label: translations.labels?.pending || "PENDING",
          className: styles.statusPending,
        };
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.student_name?.toLowerCase().includes(searchLower) ||
        item.from_date?.toLowerCase().includes(searchLower) ||
        item.to_date?.toLowerCase().includes(searchLower) ||
        item.messages?.toLowerCase().includes(searchLower),
    );
  }, [data, apiSearch]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  const handleAdd = () => {
    navigate("/user/leaveCreate");
  };

  const handleSelect = (row) => {
    navigate("/user/leaveCreate", {
      state: {
        ID: row.id,
        data: row,
      },
    });
  };

  // Table columns matching the image
  const columns = useMemo(
    () => [
      {
        accessorKey: "student_name",
        header: translations.labels?.firstName || "First Name",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {row.original.isFlag === 1 && (
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "red",
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
            )}
            <span>{row.original.student_name}</span>
          </div>
        ),
      },
      {
        accessorKey: "from_date",
        header: translations.labels?.fromDate || "From Date",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "to_date",
        header: translations.labels?.toDate || "To Date",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "no_days",
        header: translations.labels?.noOfDaysLeave || "No. of Days",
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        accessorKey: "reason",
        header: translations.labels?.reason || "Reason",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "status",
        header: translations.labels?.status || "Status",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const badge = getStatusBadge(row.original.status);
          return <span className={badge.className}>{badge.label}</span>;
        },
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleSelect(row.original)}
            title={translations.labels?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClipboardList />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.leaveList || "Leave List"}
        </h1>
      </div>

      {/* Action Buttons */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          <div className="col-lg-12 px-2 col-md-12 col-sm-12">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleAdd}
              >
                {translations.labels?.add || "ADD"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        placeholder={translations.labels.searchButton}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableSearch={false}
        enableFiltering={false}
      />
    </div>
  );
};

export default LeaveList;
